import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchProfile, logout as apiLogout } from '../services/authService';
import { User } from '../types/user';

type AuthContextShape = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (token: string, user: User) => void;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

const STORAGE_KEY = 'rt_auth';

type StoredAuth = {
  token: string;
  user: User;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const profileFetchAttempted = useRef(false);

  useEffect(() => {
    const serialized = localStorage.getItem(STORAGE_KEY);
    if (!serialized) {
      setLoading(false);
      return;
    }

    try {
      const data = JSON.parse(serialized) as StoredAuth;
      setToken(data.token);
      setUser(data.user);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback((accessToken: string, profile: User) => {
    setToken(accessToken);
    setUser(profile);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token: accessToken,
        user: profile,
      }),
    );
  }, []);

  const logout = useCallback(async () => {
    if (token) {
      try {
        await apiLogout(token);
      } catch {
        // ignore network errors on logout
      }
    }

    setToken(null);
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, [token]);

  const refreshProfile = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const updated = await fetchProfile(token);
      setUser(updated);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          token,
          user: updated,
        }),
      );
    } catch {
      await logout();
    }
  }, [logout, token]);

  useEffect(() => {
    // Only fetch profile if we have a token but no user, and we haven't already attempted
    if (token && !user && !profileFetchAttempted.current) {
      profileFetchAttempted.current = true;
      let cancelled = false;
      setLoading(true);
      refreshProfile()
        .then(() => {
          if (!cancelled) {
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    } else if (!token) {
      // Reset the flag when token is cleared
      profileFetchAttempted.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]); // refreshProfile intentionally excluded to prevent infinite loop

  const value = useMemo<AuthContextShape>(
    () => ({
      user,
      token,
      loading,
      login,
      logout,
      refreshProfile,
    }),
    [loading, login, logout, refreshProfile, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }

  return ctx;
};

